// Display helpers for the quote engine pages and the customer quote page.
import type { FullProperty } from "@widget";
import type { Category, QuoteStatus } from "./api";

/** £1,234 (whole pounds) or £1,234.50 when there are pence. */
export function gbp(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  const whole = Math.abs(v - Math.round(v)) < 0.005;
  return `${v < 0 ? "−" : ""}£${Math.abs(v).toLocaleString("en-GB", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`;
}

export const pct = (n: number | null | undefined) => `${Number(n ?? 0).toLocaleString("en-GB", { maximumFractionDigits: 1 })}%`;

export const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—");

export const STATUS_LABELS: Record<QuoteStatus, string> = { draft: "Draft", sent: "Sent", viewed: "Viewed", accepted: "Accepted", declined: "Declined" };

export const CATEGORY_LABELS: Record<Category, string> = {
  heat_pump: "Heat pumps",
  cylinder: "Cylinders",
  buffer: "Buffer tanks",
  controls: "Controls",
  radiator: "Radiators",
  ancillary: "Ancillaries",
  other: "Other",
};

type Opt<T extends string> = [T, string][];

// The Property page's options (the same wording as the Full widget, widget/src/full.ts)
export const KINDS: Opt<FullProperty["kind"]> = [
  ["house", "House"],
  ["bungalow", "Bungalow"],
  ["flat", "Flat"],
  ["maisonette", "Maisonette"],
  ["park-home", "Park home"],
];
export const FORMS: Opt<FullProperty["builtForm"]> = [
  ["detached", "Detached"],
  ["semi-detached", "Semi-detached"],
  ["end-terrace", "End terrace"],
  ["mid-terrace", "Mid terrace"],
  ["enclosed-end-terrace", "Enclosed end terrace"],
  ["enclosed-mid-terrace", "Enclosed mid terrace"],
];
export const FUELS: Opt<FullProperty["fuel"]> = [
  ["gas", "Mains gas"],
  ["oil", "Oil"],
  ["lpg", "LPG"],
  ["electric", "Electricity"],
  ["other", "Other (e.g. coal, wood)"],
];
export const HEATING_SYSTEMS: Opt<FullProperty["heatingSystem"]> = [
  ["boiler-radiators", "Boiler + radiators"],
  ["boiler-underfloor", "Boiler + underfloor"],
  ["storage-heaters", "Storage heaters"],
  ["room-heaters", "Room heaters"],
  ["heat-pump", "Heat pump"],
  ["other", "Other"],
];
export const HOT_WATER: Opt<FullProperty["hotWater"]> = [
  ["main-system", "From main system"],
  ["immersion", "Electric immersion"],
  ["other", "Other"],
];
// RdSAP 10 Table 1 age bands (England and Wales years)
export const AGE_BANDS: Opt<string> = [
  ["A", "Before 1900"],
  ["B", "1900–1929"],
  ["C", "1930–1949"],
  ["D", "1950–1966"],
  ["E", "1967–1975"],
  ["F", "1976–1982"],
  ["G", "1983–1990"],
  ["H", "1991–1995"],
  ["I", "1996–2002"],
  ["J", "2003–2006"],
  ["K", "2007–2011"],
  ["L", "2012–2022"],
  ["M", "2023 onwards"],
];

export const label = <T extends string>(opts: Opt<T>, v: T | null | undefined) => opts.find(([k]) => k === v)?.[1] ?? "";

/** A complete Property answer set, from whatever the EPC gave (the gaps get neutral defaults to review). */
export function completeProperty(p: Partial<FullProperty> & { baths?: number | null }): FullProperty {
  return {
    kind: p.kind ?? "house",
    builtForm: p.builtForm ?? "semi-detached",
    ageBand: p.ageBand ?? "D",
    storeys: p.storeys ?? 2,
    roomInRoof: p.roomInRoof ?? false,
    floorArea: p.floorArea ?? 90,
    fuel: p.fuel ?? "gas",
    heatingSystem: p.heatingSystem ?? "boiler-radiators",
    hotWater: p.hotWater ?? "main-system",
    bedrooms: p.bedrooms ?? 3,
    bathrooms: p.bathrooms ?? Math.max(1, p.baths ?? 1),
    radiators: p.radiators ?? 8,
    wallsInsulated: p.wallsInsulated ?? false,
    roofInsulated: p.roofInsulated === undefined ? true : p.roofInsulated,
    floorInsulated: p.floorInsulated === undefined ? false : p.floorInsulated,
    mainsGas: p.mainsGas === undefined ? null : p.mainsGas,
  };
}

/** Monthly repayment for a loan (APR as a percentage), the standard amortisation formula. */
export function monthlyRepayment(principal: number, aprPercent: number, months: number): number {
  if (principal <= 0 || months <= 0) return 0;
  // APR is an annual equivalent rate: the monthly rate is (1 + APR)^(1/12) − 1
  const r = Math.pow(1 + aprPercent / 100, 1 / 12) - 1;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}
